import React, { useState, useEffect } from 'react';
import Papa from 'papaparse';

const Zuordnung = ({ projectId }) => {
  // States
  const [characters, setCharacters] = useState([]);   // Figuren
  const [actors, setActors] = useState([]);          // Schauspieler
  const [assignments, setAssignments] = useState([]); // Zuordnungen
  const [newAssignment, setNewAssignment] = useState({
    figur: '',
    schauspieler: ''
  });

  // Verbesserte Version - Laden der gespeicherten Daten
  useEffect(() => {
    const loadSavedData = () => {
      if (projectId) {
        console.log('Loading data for project:', projectId);
        
        // Laden der Figuren
        const savedCharacters = localStorage.getItem(`characters_${projectId}`);
        if (savedCharacters) {
          try {
            const parsedCharacters = JSON.parse(savedCharacters);
            if (parsedCharacters && parsedCharacters.length > 0) {
              console.log('Loading characters:', parsedCharacters);
              setCharacters(parsedCharacters);
            }
          } catch (error) {
            console.error('Error loading characters:', error);
          }
        }

        // Laden der Schauspieler
        const savedActors = localStorage.getItem(`actorEnsemble_${projectId}`);
        if (savedActors) {
          try {
            const parsedActors = JSON.parse(savedActors);
            if (parsedActors && parsedActors.length > 0) {
              console.log('Loading actors:', parsedActors);
              setActors(parsedActors);
            }
          } catch (error) {
            console.error('Error loading actors:', error);
          }
        }

        // Laden der Zuordnungen
        const savedAssignments = localStorage.getItem(`assignments_${projectId}`);
        if (savedAssignments) {
          try {
            const parsedAssignments = JSON.parse(savedAssignments);
            if (parsedAssignments && parsedAssignments.length > 0) {
              console.log('Loading assignments:', parsedAssignments);
              setAssignments(parsedAssignments);
            }
          } catch (error) {
            console.error('Error loading assignments:', error);
          }
        }
      }
    };

    loadSavedData();
  }, [projectId]);

  // Verbesserte Version - Speichern bei Änderungen
  useEffect(() => {
    const saveData = () => {
      if (projectId && assignments && assignments.length > 0) {
        console.log('Saving assignments:', assignments);
        localStorage.setItem(`assignments_${projectId}`, JSON.stringify(assignments));
      }
    };

    saveData();
  }, [assignments, projectId]);

  // Handler für Eingabeänderungen
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewAssignment(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Zuordnung hinzufügen
  const addAssignment = () => {
    if (newAssignment.figur && newAssignment.schauspieler) {
      // Prüfen ob die Figur bereits zugeordnet ist
      const existingAssignment = assignments.find(
        assignment => assignment.figur === newAssignment.figur
      );

      if (existingAssignment) {
        if (!window.confirm(
          `Die Figur "${newAssignment.figur}" ist bereits ${existingAssignment.schauspieler} zugeordnet. Möchten Sie die Zuordnung überschreiben?`
        )) {
          return;
        }
        // Bestehende Zuordnung entfernen
        setAssignments(prev => prev.filter(
          assignment => assignment.figur !== newAssignment.figur
        ));
      }

      const assignmentToAdd = { 
        ...newAssignment,
        id: Date.now() 
      };
      
      setAssignments(prev => [...prev, assignmentToAdd]);
      
      // Formular zurücksetzen
      setNewAssignment({
        figur: '',
        schauspieler: ''
      });
    }
  };

  // Zuordnung löschen
  const deleteAssignment = (id) => {
    if (window.confirm('Möchten Sie diese Zuordnung wirklich löschen?')) {
      setAssignments(prev => {
        const updatedAssignments = prev.filter(assignment => assignment.id !== id);
  
        if (updatedAssignments.length === 0) {
          localStorage.removeItem(`assignments_${projectId}`);
        } else {
          localStorage.setItem(`assignments_${projectId}`, JSON.stringify(updatedAssignments));
        }
  
        return updatedAssignments;
      });
    }
  };

  // Export als CSV
  const exportToCSV = () => {
    const csv = Papa.unparse(assignments);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'zuordnungen.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Import von CSV
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    Papa.parse(file, {
      header: true,
      complete: (results) => {
        const newAssignments = results.data
          .filter(assignment => assignment.figur && assignment.schauspieler)
          .map(assignment => ({
            ...assignment,
            id: Date.now() + Math.random()
          }));
        
        setAssignments(prev => [...prev, ...newAssignments]);
      },
      error: (error) => {
        console.error('Fehler beim CSV-Import:', error);
        alert('Fehler beim Import der Excel-Datei.');
      }
    });
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-4">Zuordnung</h2>
      
      {/* Eingabeformular */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <select
          name="figur"
          value={newAssignment.figur}
          onChange={handleInputChange}
          className="border p-2"
        >
          <option value="">Figur auswählen</option>
          {characters.map((character) => (
            <option key={character.id} value={character.figur}>
              {character.figur}
            </option>
          ))}
        </select>

        <select
          name="schauspieler"
          value={newAssignment.schauspieler}
          onChange={handleInputChange}
          className="border p-2"
        >
          <option value="">Schauspieler auswählen</option>
          {actors.map((actor) => (
            <option key={actor.id} value={actor.name}>
              {actor.name}
            </option>
          ))}
        </select>

        <button
          onClick={addAssignment}
          className="bg-blue-500 text-white p-2 rounded hover:bg-blue-600"
          disabled={!newAssignment.figur || !newAssignment.schauspieler}
        >
          Zuordnung speichern
        </button>
      </div>

      {/* Import/Export Buttons */}
      <div className="mb-4 flex space-x-2">
        <input
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={handleFileUpload}
          className="hidden"
          id="csvUpload"
        />
        <label 
          htmlFor="csvUpload" 
          className="bg-green-500 text-white p-2 rounded cursor-pointer hover:bg-green-600"
        >
          Excel importieren
        </label>
        <button
          onClick={exportToCSV}
          className="bg-blue-500 text-white p-2 rounded hover:bg-blue-600"
        >
          Als Excel exportieren
        </button>
      </div>

      {/* Tabelle der Zuordnungen */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse border">
          <thead>
            <tr className="bg-gray-200">
              <th className="border p-2">Figur</th>
              <th className="border p-2">Schauspieler</th>
              <th className="border p-2">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {assignments.map((assignment) => (
              <tr key={assignment.id}>
                <td className="border p-2">{assignment.figur}</td>
                <td className="border p-2">{assignment.schauspieler}</td>
                <td className="border p-2">
                  <button
                    onClick={() => deleteAssignment(assignment.id)}
                    className="bg-red-500 text-white p-1 rounded hover:bg-red-600"
                  >
                    Löschen
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Zuordnung;